/**
 * Was aus einer Mail in Salesforce entstehen darf — Einstieg im SF-Panel.
 *
 * Das Backend-Tor (`GET /mail/contact-candidate`) kennt drei Ausgaenge, und
 * seit „Als Lead anlegen" (07.08.2026) fuehren zwei davon zu etwas Sichtbarem:
 *
 *   available                → **Kontakt anlegen** (Account ist eindeutig)
 *   reason "no_account_match" → **Als Lead anlegen** — aber NUR fuer die
 *                              Berechtigten (`GET /mail/lead-schema`)
 *   reason "already_known"   → Verweis auf den vorhandenen Lead
 *   alles andere             → nichts, wie bisher
 *
 * Die alte Regel „kein Account heisst: hier steht nichts, auch kein Hinweis
 * warum" gilt damit NICHT mehr uneingeschraenkt — sie wird gezielt fuer den
 * Lead-Fall aufgeweicht. Ihr Grund bleibt aber gueltig und traegt die neue
 * Form: ein blosser Hinweis „kein Account gefunden" wuerde zum Suchen einladen;
 * ein Knopf, der den passenden Datensatz anlegt, tut das nicht. Wer NICHT
 * berechtigt ist (in Prod 44 von 51 Nutzern), sieht weiterhin nichts —
 * der Ausblendpfad ist der haeufigste.
 *
 * Der Verweis bei „schon bekannt" erscheint nur fuer `Lead_Custom__c`. Ein
 * bekannter `Contact` oder `Lead` steht bereits im Panel darueber
 * (`msg.enrichment.sender_contact`, `app/mail/context.py:313/327`) — ein
 * zweiter Kasten daneben waere dieselbe Aussage zweimal.
 *
 * Der LLM-Vorschlag wird in beiden Faellen erst auf Klick geholt.
 *
 * Bestaetigung: `<Modal>` aus `ui/modal` (Radix, mittig zentriert, ESC +
 * Focus-Trap, `animate-modal-in` = nur Opazitaet). KEIN `window.confirm` und
 * kein Side-Drawer.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  Building2,
  Check,
  Loader2,
  Sparkles,
  UserPlus,
  Users,
} from "lucide-react";
import { api, ApiError } from "../api";
import { useT } from "../i18n";
import { katalog } from "../i18n/lang";
import { Button } from "../ui/button";
import { Modal } from "../ui/modal";

export interface ContactCandidate {
  available: boolean;
  reason?: string | null;
  email?: string | null;
  account_id?: string | null;
  account_name?: string | null;
  /** Nur bei `already_known`: welcher Datensatz die Adresse schon fuehrt. */
  existing_object?: string | null;
  existing_id?: string | null;
  existing_name?: string | null;
  existing_company?: string | null;
}

export interface ProposalField {
  key: string;
  label: string;
  value: string;
  source: "signature" | "account" | "sender" | "";
  required: boolean;
}

interface Proposal {
  email: string;
  account_id: string;
  account_name: string | null;
  fields: ProposalField[];
  warnings: string[];
}

interface CreateResult {
  id: string;
  name: string | null;
  account_id: string;
  account_name: string | null;
  mirrored: boolean;
}

// Formatregel der Salesforce-Org auf `Contact.MobilePhone`
// (Validierungsregel `Mobile_E164_on_change`, aktiv, greift bei jedem Insert
// mit gefüllter Nummer). Wortgleich zu `app/salesforce/client.py::MOBILE_E164_RE`.
//
// Diese Kopie prüft NICHTS verbindlich — das tut der Server, und er tut es
// unabhängig davon, was hier steht. Sie existiert nur, damit der Knopf zu
// bleibt, BEVOR jemand bestätigt und dafür eine Absage bekommt. Leer ist
// erlaubt (die Regel greift nur bei gefülltem Feld).
const MOBILE_E164 = /^\+[1-9][0-9]{6,14}$/;

/** Herkunfts-Marke je Feld. Der Mensch muss VOR dem Bestaetigen sehen, worauf
 *  er sich verlaesst: was die Person selbst in ihrer Signatur schreibt, was aus
 *  dem Account ergaenzt wurde und was aus dem Mail-Kopf stammt. Die Farbe steht
 *  hier, der TEXT im Katalog (`mail.kontakte.herkunft`). */
const SOURCE_CLS: Record<string, string> = {
  signature: "bg-accent-500/10 text-accent-600 dark:text-accent-400",
  account: "bg-surface-3 text-text-2",
  sender: "bg-surface-3 text-text-2",
};

function SourceBadge({ source }: { source: string }) {
  const herkunft = useT().mail.kontakte.herkunft as Record<
    string,
    string | undefined
  >;
  const cls = SOURCE_CLS[source];
  const text = herkunft[source];
  if (!cls || !text) return null;
  return (
    <span
      className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${cls}`}
    >
      {text}
    </span>
  );
}

/** Ein Feld-Eingabefeld mit Herkunfts- bzw. „geaendert"-Marke. Gemeinsam fuer
 *  Kontakt und Lead — die Herkunftsanzeige ist der Grund, warum ein Mensch das
 *  Ergebnis ueberhaupt beurteilen kann, und sie darf in keinem der beiden
 *  Modale fehlen. */
function ProposalInput({
  field,
  value,
  changed,
  readOnly,
  error,
  onChange,
}: {
  field: ProposalField;
  value: string;
  changed: boolean;
  readOnly?: boolean;
  /** Formatfehler dieses Feldes — steht DIREKT unter der Eingabe. Ein Grund,
   *  der am Fuss des Dialogs steht, ist bei zehn Feldern ausserhalb des Bildes:
   *  der Knopf ist dann grau und niemand sieht, warum. */
  error?: string;
  onChange: (v: string) => void;
}) {
  const k = useT().mail.kontakte;
  return (
    <label className="block">
      <span className="mb-0.5 flex items-center gap-1.5">
        <span className="text-xs font-medium text-text-2">
          {field.label}
          {field.required && <span className="text-danger"> *</span>}
        </span>
        {changed ? (
          <span className="shrink-0 rounded-full bg-surface-3 px-1.5 py-0.5 text-[10px] font-medium text-text-2">
            {k.geaendert}
          </span>
        ) : (
          <SourceBadge source={field.source} />
        )}
      </span>
      <input
        type="text"
        // Feldname am Element: ein Walk muss ein bestimmtes Feld ansteuern
        // koennen, ohne ueber `input[value="…"]` zu gehen — dieses Attribut
        // traegt bei einem kontrollierten React-Input nur den ERSTEN Wert und
        // ist nach der ersten Eingabe stumm.
        data-sf-field={field.key}
        value={value}
        readOnly={readOnly}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.required ? k.erforderlich : k.leer}
        className={`w-full rounded-md border bg-surface px-2.5 py-1.5 text-sm text-text placeholder:text-text-3 focus:outline-none focus:ring-2 focus:ring-accent-500/40 ${
          error || (field.required && !value.trim())
            ? "border-danger"
            : "border-border"
        } ${readOnly ? "text-text-2" : ""}`}
      />
      {error && <p className="mt-0.5 text-xs text-danger">{error}</p>}
    </label>
  );
}

// ── Wer darf ueberhaupt Leads anlegen ───────────────────────────────────────
//
// EINMAL je Sitzung, nicht je Mail: die Antwort haengt am angemeldeten Nutzer
// und nicht an der Nachricht — die Abteilung eines Menschen wechselt nicht
// zwischen zwei geoeffneten Mails. Ohne diesen Cache laege bei jedem Klick in
// die Liste ein Netz-Hop zum email-service und von dort ein SOQL auf `User`,
// fuer eine Antwort, die sich nie aendert.
//
// Gecacht wird die PROMISE, nicht der Wert: oeffnet der Nutzer zwei Mails
// schneller, als die erste Antwort da ist, teilen sich beide denselben Aufruf.
// Ein Fehler wird als `allowed: false` gecacht — fail-closed, und ohne
// Wiederhol-Sturm auf einen Dienst, der gerade ohnehin nicht kann.

interface LeadSchema {
  allowed: boolean;
  schema_name?: string | null;
  department?: string | null;
  reason?: string | null;
}

let leadSchemaCache: Promise<LeadSchema> | null = null;

function fetchLeadSchema(): Promise<LeadSchema> {
  if (!leadSchemaCache) {
    leadSchemaCache = api
      .get<LeadSchema>("/mail/lead-schema")
      .catch(() => ({ allowed: false, reason: "service_unavailable" }));
  }
  return leadSchemaCache;
}

/** `null` = noch nicht bekannt. Bewusst NICHT `false`: sonst blitzte bei den
 *  Berechtigten kurz „kein Knopf" auf und der Knopf spraenge nach. */
function useLeadAllowed(enabled: boolean): boolean | null {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    void fetchLeadSchema().then((s) => {
      if (alive) setAllowed(Boolean(s.allowed));
    });
    return () => {
      alive = false;
    };
  }, [enabled]);
  return allowed;
}

// ── Einstieg: welcher der drei Faelle ───────────────────────────────────────

export default function ContactCreate({
  candidate,
  internetMessageId,
  onCreated,
}: {
  candidate: ContactCandidate;
  internetMessageId: string;
  /** Nach erfolgreichem Anlegen — das Panel laedt die Anreicherung neu. */
  onCreated?: () => void;
}) {
  const isLeadCase =
    !candidate.available && candidate.reason === "no_account_match";
  const leadAllowed = useLeadAllowed(isLeadCase);

  if (candidate.available) {
    return (
      <ContactCreateCard
        candidate={candidate}
        internetMessageId={internetMessageId}
        onCreated={onCreated}
      />
    );
  }
  if (isLeadCase) {
    return leadAllowed ? (
      <LeadCreateCard
        candidate={candidate}
        internetMessageId={internetMessageId}
        onCreated={onCreated}
      />
    ) : null;
  }
  if (
    candidate.reason === "already_known" &&
    candidate.existing_object === "Lead_Custom__c"
  ) {
    return <KnownLeadCard candidate={candidate} />;
  }
  return null;
}

/** „Die Adresse ist schon ein Lead" — Verweis statt Knopf. Ohne ihn bliebe die
 *  naechstliegende Frage offen: wenn hier nichts angelegt werden kann, wo ist
 *  der Datensatz dann? */
function KnownLeadCard({ candidate }: { candidate: ContactCandidate }) {
  const k = useT().mail.kontakte;
  return (
    <div
      data-sf-lead-known
      className="rounded-lg border border-border bg-surface-2 px-2.5 py-2"
    >
      <div className="flex items-start gap-2">
        <Users className="mt-0.5 w-3.5 h-3.5 shrink-0 text-text-3" />
        <div className="min-w-0">
          <div className="text-xs text-text-2">{k.leadBekannt}</div>
          <div className="mt-0.5 truncate text-xs font-medium text-text">
            {candidate.existing_name || candidate.email}
          </div>
          {candidate.existing_company && (
            <div className="truncate text-xs text-text-3">
              {candidate.existing_company}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Kontakt (unveraendert im Verhalten) ─────────────────────────────────────

function ContactCreateCard({
  candidate,
  internetMessageId,
  onCreated,
}: {
  candidate: ContactCandidate;
  internetMessageId: string;
  onCreated?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [edited, setEdited] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<CreateResult | null>(null);
  const k = useT().mail.kontakte;

  const start = useCallback(async () => {
    setOpen(true);
    setLoading(true);
    setError(null);
    setDone(null);
    setEdited(new Set());
    try {
      const p = await api.post<Proposal>("/mail/contact-proposal", {
        internet_message_id: internetMessageId,
      });
      setProposal(p);
      setValues(Object.fromEntries(p.fields.map((f) => [f.key, f.value])));
    } catch (e) {
      const err = e as ApiError;
      const kk = katalog().mail.kontakte;
      setError(
        err.status === 502
          ? kk.kontaktVorschlagFehler502
          : err.message || kk.kontaktVorschlagFehler
      );
    } finally {
      setLoading(false);
    }
  }, [internetMessageId]);

  const submit = useCallback(async () => {
    if (!proposal) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.post<CreateResult>("/mail/salesforce-contacts", {
        internet_message_id: internetMessageId,
        account_id: proposal.account_id,
        fields: values,
      });
      setDone(res);
      // `onCreated` NICHT hier — gleiche Begruendung wie im Lead-Pfad unten:
      // der Aufrufer laedt damit das Tor neu und setzt `contactCandidate` auf
      // null, wodurch diese Komponente samt Modal MITTEN in der Erfolgsmeldung
      // verschwindet. Der Panel-Refresh passiert beim Schliessen.
    } catch (e) {
      const err = e as ApiError;
      // `err.message` traegt bereits den lesbaren Serverfehler (Dublette,
      // Pflichtfeld, Laenge) — unveraendert zeigen statt zu verallgemeinern.
      setError(err.message || katalog().mail.kontakte.kontaktAnlegenFehler);
    } finally {
      setSaving(false);
    }
  }, [proposal, values, internetMessageId, onCreated]);

  const lastName = (values.LastName ?? "").trim();
  const mobil = (values.MobilePhone ?? "").trim();
  const mobilUngueltig = mobil !== "" && !MOBILE_E164.test(mobil);

  return (
    <>
      <div
        data-sf-contact-create
        className="rounded-lg border border-dashed border-border bg-surface px-2.5 py-2"
      >
        <div className="text-xs text-text-2">{k.kontaktEinstieg}</div>
        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-text-3">
          <Building2 className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate" title={candidate.account_name ?? undefined}>
            {candidate.account_name}
          </span>
        </div>
        <Button
          variant="secondary"
          size="sm"
          className="mt-2 w-full"
          icon={<UserPlus className="w-3.5 h-3.5" />}
          onClick={() => void start()}
        >
          {k.kontaktAnlegen}
        </Button>
      </div>

      {open && (
        <ContactCreateModal
          onClose={() => {
            setOpen(false);
            // Erst jetzt das Tor neu laden: der Absender ist danach „schon
            // bekannt", die Karte verschwindet — richtig, aber eben erst,
            // nachdem der Mensch die Meldung gesehen hat.
            if (done) onCreated?.();
          }}
          candidate={candidate}
          loading={loading}
          saving={saving}
          proposal={proposal}
          values={values}
          edited={edited}
          error={error}
          done={done}
          lastNameMissing={!lastName}
          mobilUngueltig={mobilUngueltig}
          onChange={(key, v) => {
            setValues((prev) => ({ ...prev, [key]: v }));
            setEdited((prev) => new Set(prev).add(key));
          }}
          onSubmit={() => void submit()}
        />
      )}
    </>
  );
}

function ContactCreateModal({
  onClose,
  candidate,
  loading,
  saving,
  proposal,
  values,
  edited,
  error,
  done,
  lastNameMissing,
  mobilUngueltig,
  onChange,
  onSubmit,
}: {
  onClose: () => void;
  candidate: ContactCandidate;
  loading: boolean;
  saving: boolean;
  proposal: Proposal | null;
  values: Record<string, string>;
  edited: Set<string>;
  error: string | null;
  done: CreateResult | null;
  lastNameMissing: boolean;
  /** Formatregel der Org auf `MobilePhone` verletzt → Anlegen bleibt zu. */
  mobilUngueltig: boolean;
  onChange: (key: string, value: string) => void;
  onSubmit: () => void;
}) {
  const k = useT().mail.kontakte;
  const ct = useT().common;
  // Bewusst der gemeinsame Modal-Wrapper: zentriert, ESC, Focus-Trap.
  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      icon={<UserPlus className="w-4 h-4" />}
      title={k.kontaktModalTitel}
      description={
        done
          ? undefined
          : k.kontaktModalBeschreibung(candidate.account_name ?? "")
      }
      footer={
        done ? (
          <Button variant="primary" size="sm" onClick={onClose}>
            {ct.schliessen}
          </Button>
        ) : (
          <>
            <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
              {ct.abbrechen}
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={saving}
              disabled={loading || !proposal || lastNameMissing || mobilUngueltig}
              icon={<Check className="w-3.5 h-3.5" />}
              onClick={onSubmit}
            >
              {k.kontaktAnlegen}
            </Button>
          </>
        )
      }
    >
      {done ? (
        <div className="space-y-2">
          <div className="flex items-start gap-2 rounded-lg border border-border bg-surface-2 p-3">
            <Check className="mt-0.5 w-4 h-4 shrink-0 text-success" />
            <div className="text-sm text-text">
              <div className="font-medium">
                {k.kontaktAngelegt(done.name || k.kontaktFallbackName)}
              </div>
              <div className="mt-0.5 text-xs text-text-2">
                {k.kontaktAccount(done.account_name ?? done.account_id)}
              </div>
            </div>
          </div>
          {!done.mirrored && (
            <div className="text-xs text-text-3">{k.kontaktNichtGespiegelt}</div>
          )}
        </div>
      ) : loading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="h-9 rounded-md bg-surface-3 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {proposal?.warnings.map((w, i) => (
            <div
              key={i}
              className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 p-2.5"
            >
              <AlertTriangle className="mt-0.5 w-4 h-4 shrink-0 text-warning" />
              <span className="text-xs text-text-2">{w}</span>
            </div>
          ))}

          {error && (
            <div className="rounded-lg border border-danger/40 bg-danger/10 p-2.5 text-xs text-text">
              {error}
            </div>
          )}

          {proposal && (
            <>
              <p className="text-xs text-text-3">{k.pruefenHinweis}</p>
              <div className="space-y-2">
                {proposal.fields.map((f) => (
                  <ProposalInput
                    key={f.key}
                    field={f}
                    value={values[f.key] ?? ""}
                    changed={edited.has(f.key)}
                    readOnly={f.key === "Email"}
                    error={
                      f.key === "MobilePhone" && mobilUngueltig
                        ? k.mobilE164Hinweis
                        : undefined
                    }
                    onChange={(v) => onChange(f.key, v)}
                  />
                ))}
              </div>
              {lastNameMissing && (
                <p className="text-xs text-danger">{k.kontaktOhneNachname}</p>
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

// ── Lead ────────────────────────────────────────────────────────────────────

interface LeadProposal {
  email: string;
  fields: ProposalField[];
  warnings: string[];
}

interface LeadCreateResult {
  lead_id: string;
  email: string | null;
  schema_name: string | null;
  owner_id: string | null;
  interest_type: string | null;
  status: string;
  job_id: string | null;
}

interface ResearchState {
  /** running = wir fragen noch · done · timeout · unknown = keine Auskunft */
  phase: "running" | "done" | "timeout" | "unknown" | "failed";
  filled: number;
}

// Die Recherchekette ist gemessen, nicht geschaetzt: 6,5 s Firma + 10,4 s
// Wettbewerb + 7,1 s Zusammenfassung = ~24 s. Alle 3 s fragen und nach 90 s
// aufhoeren ist knapp das Dreifache — genug fuer einen langsamen Tag, wenig
// genug, dass ein haengender Job nicht endlos Anfragen erzeugt.
const RESEARCH_POLL_MS = 3000;
const RESEARCH_MAX_MS = 90000;

function LeadCreateCard({
  candidate,
  internetMessageId,
  onCreated,
}: {
  candidate: ContactCandidate;
  internetMessageId: string;
  onCreated?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [proposal, setProposal] = useState<LeadProposal | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [edited, setEdited] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<LeadCreateResult | null>(null);
  const [research, setResearch] = useState<ResearchState | null>(null);
  const k = useT().mail.kontakte;

  const start = useCallback(async () => {
    setOpen(true);
    setLoading(true);
    setError(null);
    setDone(null);
    setResearch(null);
    setEdited(new Set());
    try {
      const p = await api.post<LeadProposal>("/mail/lead-proposal", {
        internet_message_id: internetMessageId,
      });
      setProposal(p);
      setValues(Object.fromEntries(p.fields.map((f) => [f.key, f.value])));
    } catch (e) {
      const err = e as ApiError;
      setError(err.message || katalog().mail.kontakte.leadVorschlagFehler);
    } finally {
      setLoading(false);
    }
  }, [internetMessageId]);

  const submit = useCallback(async () => {
    if (!proposal) return;
    setSaving(true);
    setError(null);
    try {
      const res = await api.post<LeadCreateResult>("/mail/leads", {
        internet_message_id: internetMessageId,
        fields: values,
        research: true,
      });
      setDone(res);
      if (res.job_id) setResearch({ phase: "running", filled: 0 });
      // `onCreated` NICHT hier: der Aufrufer laedt damit das Tor neu und setzt
      // dabei `contactCandidate` auf null — diese Komponente (samt Modal)
      // verschwindet dann MITTEN in der Erfolgsmeldung, und die Recherche-Zeile
      // haette nie eine Chance, ihre ~25 s abzuwarten. Der Panel-Refresh
      // passiert deshalb beim Schliessen (s. `onClose` unten).
    } catch (e) {
      const err = e as ApiError;
      // `err.message` traegt den deutschen Satz des Servers (Dublette mit Namen,
      // Abteilung nicht freigeschaltet, Mail nicht mehr da) — unveraendert
      // zeigen statt zu verallgemeinern.
      setError(err.message || katalog().mail.kontakte.leadAnlegenFehler);
    } finally {
      setSaving(false);
    }
  }, [proposal, values, internetMessageId, onCreated]);

  const lastName = (values.Last_Name__c ?? "").trim();

  return (
    <>
      <div
        data-sf-lead-create
        className="rounded-lg border border-dashed border-border bg-surface px-2.5 py-2"
      >
        <div className="text-xs text-text-2">{k.leadEinstieg}</div>
        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-text-3">
          <Sparkles className="w-3.5 h-3.5 shrink-0" />
          <span className="truncate" title={candidate.email ?? undefined}>
            {candidate.email}
          </span>
        </div>
        <Button
          variant="secondary"
          size="sm"
          className="mt-2 w-full"
          icon={<Users className="w-3.5 h-3.5" />}
          onClick={() => void start()}
        >
          {k.leadAnlegen}
        </Button>
      </div>

      {open && (
        <LeadCreateModal
          onClose={() => {
            setOpen(false);
            // Erst jetzt das Tor neu laden: der Absender ist danach „schon
            // bekannt", die Karte verschwindet — richtig, aber eben erst,
            // nachdem der Mensch die Meldung gesehen hat.
            if (done) onCreated?.();
          }}
          loading={loading}
          saving={saving}
          proposal={proposal}
          values={values}
          edited={edited}
          error={error}
          done={done}
          research={research}
          onResearch={setResearch}
          lastNameMissing={!lastName}
          onChange={(key, v) => {
            setValues((prev) => ({ ...prev, [key]: v }));
            setEdited((prev) => new Set(prev).add(key));
          }}
          onSubmit={() => void submit()}
        />
      )}
    </>
  );
}

/** Fragt den Recherche-Job, bis er fertig ist oder die Obergrenze erreicht ist.
 *
 *  Der Job gehoert NICHT diesem Modal: er laeuft im Dienst zu Ende, auch wenn
 *  der Nutzer weiterklickt. Deshalb bricht das Fragen beim Schliessen einfach
 *  ab (`cancelled`), ohne irgendetwas zu stornieren — und deshalb steht auf dem
 *  Bildschirm vorher schon, dass der Lead ANGELEGT ist. Nach `RESEARCH_MAX_MS`
 *  wird nicht weitergefragt, sondern gesagt, dass die Recherche noch laeuft:
 *  eine Schleife ohne Ende waere hier ein Fehler, ein stilles Aufhoeren eine
 *  Luege. */
function useResearchPolling(
  done: LeadCreateResult | null,
  onResearch: (s: ResearchState) => void
) {
  const cb = useRef(onResearch);
  cb.current = onResearch;

  useEffect(() => {
    const jobId = done?.job_id;
    const leadId = done?.lead_id;
    if (!jobId || !leadId) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const deadline = Date.now() + RESEARCH_MAX_MS;

    const tick = async () => {
      if (cancelled) return;
      try {
        const r = await api.get<{
          status: string;
          fields_filled: string[];
          error: string | null;
        }>(
          `/mail/lead-research?lead_id=${encodeURIComponent(
            leadId
          )}&job_id=${encodeURIComponent(jobId)}`
        );
        if (cancelled) return;
        if (r.status === "completed") {
          cb.current({ phase: "done", filled: r.fields_filled?.length ?? 0 });
          return;
        }
        if (r.status === "failed") {
          cb.current({ phase: "failed", filled: 0 });
          return;
        }
        if (r.status === "unknown") {
          cb.current({ phase: "unknown", filled: 0 });
          return;
        }
      } catch {
        // Eine einzelne verlorene Abfrage ist kein Ergebnis — weiterfragen,
        // bis die Obergrenze greift.
      }
      if (cancelled) return;
      if (Date.now() >= deadline) {
        cb.current({ phase: "timeout", filled: 0 });
        return;
      }
      timer = setTimeout(() => void tick(), RESEARCH_POLL_MS);
    };

    timer = setTimeout(() => void tick(), RESEARCH_POLL_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [done?.job_id, done?.lead_id]);
}

function ResearchLine({ research }: { research: ResearchState }) {
  const k = useT().mail.kontakte;
  if (research.phase === "running") {
    return (
      <div className="flex items-center gap-2 text-xs text-text-2">
        <Loader2 className="w-3.5 h-3.5 shrink-0 animate-spin text-accent-500" />
        {k.rechercheLaeuft}
      </div>
    );
  }
  if (research.phase === "done") {
    return (
      <div className="flex items-center gap-2 text-xs text-text-2">
        <Check className="w-3.5 h-3.5 shrink-0 text-success" />
        {k.rechercheFertig(research.filled)}
      </div>
    );
  }
  if (research.phase === "failed") {
    return <div className="text-xs text-text-3">{k.rechercheFehler}</div>;
  }
  // timeout | unknown — beide Male gilt dasselbe, und das Wichtigste zuerst:
  // der Lead EXISTIERT. Nur die Auskunft ueber die Recherche fehlt.
  return <div className="text-xs text-text-3">{k.rechercheOffen}</div>;
}

function LeadCreateModal({
  onClose,
  loading,
  saving,
  proposal,
  values,
  edited,
  error,
  done,
  research,
  onResearch,
  lastNameMissing,
  onChange,
  onSubmit,
}: {
  onClose: () => void;
  loading: boolean;
  saving: boolean;
  proposal: LeadProposal | null;
  values: Record<string, string>;
  edited: Set<string>;
  error: string | null;
  done: LeadCreateResult | null;
  research: ResearchState | null;
  onResearch: (s: ResearchState) => void;
  lastNameMissing: boolean;
  onChange: (key: string, value: string) => void;
  onSubmit: () => void;
}) {
  const k = useT().mail.kontakte;
  const ct = useT().common;
  useResearchPolling(done, onResearch);

  const leadName = [values.First_Name__c, values.Last_Name__c]
    .filter((p) => p && p.trim())
    .join(" ");

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      icon={<Users className="w-4 h-4" />}
      title={k.leadModalTitel}
      description={
        done ? undefined : proposal ? k.leadModalBeschreibung(proposal.email) : undefined
      }
      footer={
        done ? (
          <Button variant="primary" size="sm" onClick={onClose}>
            {ct.schliessen}
          </Button>
        ) : (
          <>
            <Button variant="secondary" size="sm" onClick={onClose} disabled={saving}>
              {ct.abbrechen}
            </Button>
            <Button
              variant="primary"
              size="sm"
              loading={saving}
              disabled={loading || !proposal || lastNameMissing}
              icon={<Check className="w-3.5 h-3.5" />}
              onClick={onSubmit}
            >
              {k.leadKnopf}
            </Button>
          </>
        )
      }
    >
      {done ? (
        <div className="space-y-2">
          <div className="flex items-start gap-2 rounded-lg border border-border bg-surface-2 p-3">
            <Check className="mt-0.5 w-4 h-4 shrink-0 text-success" />
            <div className="min-w-0 text-sm text-text">
              <div className="font-medium">
                {k.leadAngelegt(leadName || k.leadFallbackName)}
              </div>
              <div className="mt-0.5 truncate text-xs text-text-2">
                {done.email} · {done.lead_id}
              </div>
            </div>
          </div>
          {research && <ResearchLine research={research} />}
        </div>
      ) : loading ? (
        <div className="space-y-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-9 rounded-md bg-surface-3 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {proposal?.warnings.map((w, i) => (
            <div
              key={i}
              className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 p-2.5"
            >
              <AlertTriangle className="mt-0.5 w-4 h-4 shrink-0 text-warning" />
              <span className="text-xs text-text-2">{w}</span>
            </div>
          ))}

          {error && (
            <div className="rounded-lg border border-danger/40 bg-danger/10 p-2.5 text-xs text-text">
              {error}
            </div>
          )}

          {proposal && (
            <>
              <p className="text-xs text-text-3">{k.leadPruefenHinweis}</p>
              <div className="space-y-2">
                {proposal.fields.map((f) => (
                  <ProposalInput
                    key={f.key}
                    field={f}
                    value={values[f.key] ?? ""}
                    changed={edited.has(f.key)}
                    onChange={(v) => onChange(f.key, v)}
                  />
                ))}
              </div>
              {lastNameMissing && (
                <p className="text-xs text-danger">{k.leadOhneNachname}</p>
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
